import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { Markdown } from "@/components/markdown/Markdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { TabsDummyButton } from "@/components/ui/tabs";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import {
  GetRagSearchResponseDto,
  kitControllerRagSearch,
} from "@/lib/gen-brain";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

const LABEL_CLASSNAME = "font-bold w-12 shrink-0";

export const KitDebugPage: React.FC = () => {
  const groupSlug = useGroupSlug();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GetRagSearchResponseDto>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expand, setExpand] = useState(true);

  const handleSearch = async (expandResults = expand) => {
    if (!groupUuid || !kitUuid) {
      return;
    }
    setIsLoading(true);
    setResults([]);
    const response = await kitControllerRagSearch({
      path: {
        groupUuid: groupUuid,
        kitUuid: kitUuid,
      },
      query: {
        filter: {
          query: query,
          expand: expandResults,
        },
      },
    });
    if (response.data) {
      setResults(response.data);
    }
    setIsLoading(false);
  };

  const handleExpandChange = (expandResults: boolean) => {
    setExpand(expandResults);
    if (query) {
      void handleSearch(expandResults);
    }
  };

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Kit Debug</PageHeader.Title>
      </PageHeader>
      <PageBody innerClassName="gap-8">
        <div className="flex flex-row items-center gap-8">
          <div className="flex w-1/2 flex-row items-center gap-4">
            <Input
              placeholder="RAG Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  void handleSearch();
                }
              }}
              disabled={isLoading}
            />
            <Button
              disabled={isLoading || !query}
              onClick={() => void handleSearch()}
            >
              Search
            </Button>
          </div>
          <div className="flex w-1/2 flex-row items-center gap-4">
            <TabsDummyButton
              isActive={expand}
              onClick={() => handleExpandChange(true)}
            >
              Expanded
            </TabsDummyButton>
            <TabsDummyButton
              isActive={!expand}
              onClick={() => handleExpandChange(false)}
            >
              Original
            </TabsDummyButton>
          </div>
        </div>
        <div className="flex flex-col gap-8">
          {results.map((result, index) => {
            const spanNode = result.expandedFileNode ?? result.fileNode;
            return (
              <>
                {index > 0 && <Separator />}
                <div key={index} className="flex flex-col gap-4">
                  <div className="flex flex-row gap-4">
                    <div className="flex w-1/2 flex-col gap-1 text-xs">
                      <h3 className="text-lg font-bold">
                        #{index + 1}: {result.fileNode.title}
                      </h3>
                      <div className="flex flex-row gap-4">
                        <div className={LABEL_CLASSNAME}>Score:</div>
                        <div>{result.score.toFixed(4)}</div>
                      </div>
                      <div className="flex flex-row gap-4">
                        <div className={LABEL_CLASSNAME}>File:</div>
                        <Link
                          to="/w/$groupSlug/k/$kitUuid/folders/$folderUuid/files/$fileUuid/debug"
                          params={{
                            groupSlug,
                            kitUuid: kitUuid!,
                            folderUuid: result.file.folder_uuid,
                            fileUuid: result.file.uuid,
                          }}
                          className="font-bold"
                        >
                          {result.file.name}
                        </Link>
                      </div>
                      <div className="flex flex-row gap-4">
                        <div className={LABEL_CLASSNAME}>HyDE:</div>
                        <div>{result.query}</div>
                      </div>
                      <div className="text-foreground-5">Original</div>
                      <div className="flex flex-row gap-4">
                        <div className={LABEL_CLASSNAME}>Pages:</div>
                        <div>
                          {result.fileNode.start_page}
                          {result.fileNode.end_page !==
                          result.fileNode.start_page
                            ? ` - ${result.fileNode.end_page}`
                            : ""}
                        </div>
                      </div>
                      <div className="flex flex-row gap-4">
                        <div className={LABEL_CLASSNAME}>Index:</div>
                        <div>
                          {result.fileNode.start_index} -{" "}
                          {result.fileNode.end_index} ({result.fileNode.length})
                        </div>
                      </div>
                      {result.expandedFileNode && (
                        <>
                          <div className="text-foreground-5">Expanded</div>
                          <div className="flex flex-row gap-4">
                            <div className={LABEL_CLASSNAME}>Pages:</div>
                            <div>
                              {spanNode.start_page}
                              {spanNode.end_page !== spanNode.start_page
                                ? ` - ${spanNode.end_page}`
                                : ""}
                            </div>
                          </div>
                          <div className="flex flex-row gap-4">
                            <div className={LABEL_CLASSNAME}>Index:</div>
                            <div>
                              {spanNode.start_index} - {spanNode.end_index} (
                              {spanNode.length})
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="w-1/2">
                      <Markdown
                        className="bg-background-3 max-h-64 w-full overflow-y-auto p-4"
                        allowRawHtml
                      >
                        {result.markdown}
                      </Markdown>
                    </div>
                  </div>
                </div>
              </>
            );
          })}
        </div>
      </PageBody>
    </Page>
  );
};
